import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { colors, RISK_COLORS } from '@lmc/common/constants'
import { isBrowser, isTablet } from 'react-device-detect'
import { ProfilePicture } from '../../../generic'
import { capitalize } from 'lodash'
import dayjs from 'dayjs'

const isDesktop = isBrowser || isTablet

interface RiskAssessmentOutcomeProps {
  resident: {
    name: {
      first: string
      last: string
    }
    dateOfBirth: string
    picture: string
    status: string
  }
  hasActions?: boolean
  risk: {
    level: 'na' | 'low' | 'medium' | 'high' | 'severe' | 'incomplete'
    score: number
  }
}

class RiskAssessmentOutcome extends Component<RiskAssessmentOutcomeProps> {
  renderScore = (risk: RiskAssessmentOutcomeProps['risk']) => (
    <div
      id="risk-score"
      style={{
        ...styles.score,
        background:
          risk.level === 'incomplete' ? colors.grey : RISK_COLORS[risk.level],
      }}
    >
      {risk.score}
    </div>
  )

  renderText = (
    resident: RiskAssessmentOutcomeProps['resident'],
    risk: RiskAssessmentOutcomeProps['risk']
  ) => {
    const dateOfBirth = dayjs(resident.dateOfBirth).format('Do MMMM YYYY')
    const age = dayjs().diff(dayjs(resident.dateOfBirth), 'years')
    const fullName = `${resident.name.first} ${resident.name.last}`

    const outcomeText = this.props.hasActions
      ? // @ts-ignore
        this.props.readOnly
        ? // @ts-ignore
          RISK_OUTCOMES_READ_ONLY[risk.level]
        : RISK_OUTCOMES[risk.level]
      : // @ts-ignore
        RISK_OUTCOMES_NO_ACTIONS[risk.level]

    return (
      <div style={styles.outcomesTextContainer}>
        <span
          style={{
            ...styles.outcomesText,
            color: RISK_COLORS[risk.level],
          }}
        >
          {outcomeText}
        </span>
        <div style={styles.residentInfoContainer}>
          {!isDesktop && (
            <ProfilePicture
              url={resident.picture}
              style={styles.residentImageSmall}
              type="handover__small"
            />
          )}
          <span style={styles.residentInfo}>
            {fullName}, {dateOfBirth} ({age}) •{` `}
            <span style={{ color: colors.successgreen }}>
              {capitalize(resident.status)}
            </span>
          </span>
        </div>
      </div>
    )
  }

  render() {
    const { resident, risk } = this.props
    if (!resident || !risk || !risk.level) return null

    return (
      <div
        className="lmc-card"
        style={{
          ...styles.wrapper,
          display: isDesktop ? 'flex' : 'block',
        }}
      >
        {this.renderScore(risk)}
        {isDesktop && (
          <ProfilePicture
            url={resident.picture}
            type="large"
            style={styles.residentImageLarge}
          />
        )}
        {this.renderText(resident, risk)}
      </div>
    )
  }
}

const RISK_OUTCOMES = {
  incomplete: '"Fill out the assessment to see my Risk Outcome Score".',
  na: '"I am not at risk. Please describe how best to care for me in the box below".',
  low: '"I am at low risk. Please describe how best to care for me in the box below".',
  medium:
    '"I am at moderate risk. Please describe how best to care for me in the box below".',
  high: '"I am at high risk. Please describe how best to care for me".',
  severe:
    '"I am at severe risk. Please describe how best to care for me in the box below".',
}

const RISK_OUTCOMES_READ_ONLY = {
  na: '"I am not at risk. How best to care for me is described in the box below".',
  low: '"I am at low risk. How best to care for me is described in the box below".',
  medium:
    '"I am at moderate risk. How best to care for me is described in the box below".',
  high: '"I am at high risk. How best to care for me is described in the box below".',
  severe:
    '"I am at severe risk. How best to care for me is described in the box below".',
}

const RISK_OUTCOMES_NO_ACTIONS = {
  na: '"I am not at risk"',
  low: '"I am at low risk"',
  medium: '"I am at moderate risk"',
  high: '"I am at high risk"',
  severe: '"I am at severe risk"',
}

const styles = {
  outcomesText: {
    display: 'block',
    marginTop: 5,
    fontWeight: 500,
    fontSize: isDesktop ? 28 : 18,
    overflow: 'auto',
  },
  outcomesTextContainer: {
    width: '100%',
  },
  residentImageLarge: {
    flexShrink: 0,
    margin: '0px 24px',
  },
  residentImageSmall: {
    marginRight: 15,
  },
  residentInfo: {
    color: colors.black,
    fontSize: 18,
    fontWeight: 400,
    marginTop: 0,
  },
  residentInfoContainer: {
    display: 'flex',
    marginTop: 0,
  },
  score: {
    borderRadius: 10,
    color: 'white',
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    fontWeight: 'bold',
    height: isDesktop ? 74 : 60,
    width: isDesktop ? 74 : '100%',
    marginTop: 'auto',
    marginBottom: 'auto',
  },
  wrapper: {
    padding: 24,
  },
}

export default RiskAssessmentOutcome
